import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { CancelToken, isCancel } from 'axios';
import moment from 'moment';
import { ModalBody, ModalHeader, Progress } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faCheckCircle,
  faMinus,
  faStopwatch,
} from '@fortawesome/free-solid-svg-icons';
import css from 'styled-jsx/css';

import SchedulesService from '../../Services/SchedulesService';
import ModalLayout from '../ModalLayout';

export default memo(MobileMaintenanceCardDetails);

const iconDisplayMap = new Map([
  ['upcoming', { icon: faExclamationTriangle, color: 'warning-yellow' }],
  ['due', { icon: faStopwatch, color: 'active-dm' }],
  ['complete', { icon: faCheckCircle, color: 'alert-confirmation' }],
]);

const frequencyDisplayTextMap = new Map([
  ['weekly', 'week'],
  ['monthly', 'month'],
  ['yearly', 'year'],
]);

const reminderTypeColorMap = new Map([
  ['Inspection', 'label-3'],
  ['Maintenance', 'highlight-blue'],
  ['Parts', 'label-5'],
  ['Sales', 'cyan-blue'],
  ['Service', 'darker-blue'],
  ['Warranty', 'label-2'],
]);

const { className: progressClass, styles: progressStyles } = css.resolve`
  div.progress {
    height: 0.5rem;
  }
`;
function MobileMaintenanceCardDetails(props) {
  const [partsQuoteRequestPending, togglePartsQuoteRequestPending] =
    useState(false);
  const [serviceManualRequestPending, toggleServiceManualRequestPending] =
    useState(false);

  const source = CancelToken.source();
  const service = new SchedulesService();

  const partsQuote = props.instance?.reminder?.attachment ?? '';
  const { icon, color } = iconDisplayMap.get(props.instanceType) ?? {};
  const { interval, type } = props.instance?.reminder?.trigger ?? {};

  const overdueDisplayValue =
    Math.round(
      (props.signalValue - props.instance?.value + Number.EPSILON) * 100
    ) / 100;
  const upcomingDisplayValue =
    Math.round(
      (props.instance?.value - props.signalValue + Number.EPSILON) * 100
    ) / 100;
  const recurringDisplayText = props.instance?.reminder?.recurring
    ? 'Every'
    : 'Once at';
  const frequencyDisplayText =
    frequencyDisplayTextMap.get(props.instance?.reminder?.trigger?.frequency) ??
    '';

  const downloadPartsQuote = async () => {
    try {
      togglePartsQuoteRequestPending(true);
      const { attachment } = await service.getReminderAttachment({
        scheduleId: props.instance?.scheduleId,
        reminderId: props.instance?.reminder?.id,
        orgID: props.instance?.orgId,
        cancelToken: source.token,
      });
      togglePartsQuoteRequestPending(false);
      window.open(attachment, '_blank');
    } catch (err) {
      if (isCancel(err)) return;
      togglePartsQuoteRequestPending(false);
    }
  };

  const downloadServiceManual = async () => {
    try {
      toggleServiceManualRequestPending(true);
      const { serviceManual } = await service.getServiceManual({
        scheduleId: props.instance?.scheduleId,
        orgID: props.instance?.orgId,
        cancelToken: source.token,
      });
      toggleServiceManualRequestPending(false);
      window.open(serviceManual, '_blank');
    } catch (err) {
      if (isCancel(err)) return;
      toggleServiceManualRequestPending(false);
    }
  };

  return (
    <ModalLayout centered isOpen={props.isOpen} scrollable>
      <ModalHeader
        className="border-bottom-0"
        toggle={() => props.toggleModal(!props.isOpen)}
      >
        <p className="font-weight-normal">Review Details</p>
      </ModalHeader>
      <ModalBody className="d-flex flex-column text-muted align-items-center">
        <div className="w-100 mb-4">
          <p className="text-third-gray font-weight-light three-quarter-text">
            Name
          </p>
          <div className="d-flex align-items-center">
            <FontAwesomeIcon
              className={`text-${color}`}
              icon={icon}
              size="lg"
            />
            <p className="text-muted font-weight-bolder ml-2 mb-0">
              {props.instance?.reminder?.name}
            </p>
          </div>
          {type === 'conditional' && props.instanceType === 'complete' ? (
            <p className={`mt-2 mb-0 text-${color}`}>Service Completed</p>
          ) : type === 'conditional' ? (
            <div className="mt-2">
              <Progress
                className={`my-1 ${progressClass}`}
                color={color}
                style={{ height: '0.5rem' }}
                value={
                  props.instanceType === 'due'
                    ? 100
                    : (props.signalValue / props.instance?.value) * 100
                }
              />
              <div
                className={
                  props.instanceType === 'complete' ? 'd-none' : 'd-block'
                }
              >
                <span className={`text-${color} three-quarter-text`}>
                  {props.instanceType === 'due'
                    ? `${overdueDisplayValue} hours overdue`
                    : `${upcomingDisplayValue} hours remaining`}
                </span>
              </div>
              {progressStyles}
            </div>
          ) : (
            <>
              <p className="mt-2 mb-0 three-quarter-text text-text-light-gray">
                {`${recurringDisplayText ?? ''} ${
                  interval > 1 ? interval : ''
                } ${
                  interval > 1
                    ? `${frequencyDisplayText}s`
                    : frequencyDisplayText
                }`}
              </p>
              <div
                className={`mt-2 rounded-lg border border-${color} d-flex flex-column align-items-center justify-content-around`}
              >
                {props.instanceType === 'complete' ? (
                  <p className={`text-${color} three-quarter-text`}>
                    Service Complete
                  </p>
                ) : (
                  <>
                    <p className={`mb-0 text-${color}`}>
                      {Math.abs(props.timeDiff)}
                    </p>
                    <p className="three-quarter-text mb-0 text-text-light-gray text-center text-nowrap">
                      {props.timeDiffDisplayText}
                    </p>
                  </>
                )}
              </div>
            </>
          )}
        </div>
        <div className="w-100 mb-4">
          <p className="text-third-gray font-weight-light three-quarter-text">
            Schedule
          </p>
          <div className="d-flex align-items-center">
            <Link
              className="btn btn-link text-left p-0 font-weight-bolder"
              to={`/schedule/${props.instance?.scheduleId}`}
            >
              {props.instance?.scheduleName || ''}
            </Link>
          </div>
        </div>
        <div className="w-100 mb-4">
          <p className="text-third-gray font-weight-light three-quarter-text">
            Date Trigger
          </p>
          <p className="font-weight-bolder">
            {moment(props.instance?.createdAt).format('MMMM Do, Y')}
          </p>
        </div>
        <div className="w-100 mb-4">
          <p className="text-third-gray font-weight-light three-quarter-text">
            Date Serviced
          </p>
          <p className="font-weight-bolder">
            {props.instance?.serviceDate
              ? moment(props.instance.serviceDate).format('MMMM Do, Y')
              : ''}
          </p>
        </div>
        <div className="w-100 mb-4">
          <p className="text-third-gray font-weight-light three-quarter-text">
            Parts Revenue
          </p>
          <p className="font-weight-bolder text-alert-confirmation">
            {props.instance?.partsCost ? (
              `${props.instance.partsCost}`
            ) : (
              <FontAwesomeIcon
                className="text-alert-confirmation"
                icon={faMinus}
                size="lg"
              />
            )}
          </p>
        </div>

        <div className="w-100 mb-4">
          <p className="text-third-gray font-weight-light three-quarter-text">
            Label
          </p>
          <div
            className={`badge badge-primary text-capitalize px-2 py-2 nine-tenth-text font-weight-normal bg-${reminderTypeColorMap.get(
              props.instance?.reminder?.type
            )}`}
          >
            {props.instance?.reminder?.type}
          </div>
        </div>
        <div className="w-100 mb-4">
          <p className="text-third-gray font-weight-light three-quarter-text">
            Parts Quote
          </p>
          <div className="text-icon-link-gray">
            {partsQuote ? (
              <>
                <button
                  className="btn btn-block modal-button-primary-outline nine-tenth-text text-center py-1 px-4 rounded"
                  disabled={partsQuoteRequestPending}
                  onClick={downloadPartsQuote}
                >
                  Download Parts Quote
                </button>

                <p className="three-quarter-text text-truncate text-center mt-1">
                  {partsQuote.substring(27, partsQuote.length)}
                </p>
              </>
            ) : (
              <p>No Parts Quote</p>
            )}
          </div>
        </div>
        <div className="w-100 mb-4">
          <p className="text-third-gray font-weight-light three-quarter-text">
            Service Manual
          </p>
          <div className="text-icon-link-gray">
            {props.instance?.serviceManual ? (
              <>
                <button
                  className="btn btn-block modal-button-primary-outline nine-tenth-text text-center py-1 px-4 rounded"
                  disabled={serviceManualRequestPending}
                  onClick={downloadServiceManual}
                >
                  Download Service Manual
                </button>

                <p className="three-quarter-text text-truncate text-center mt-1">
                  {props.instance?.serviceManual.substring(
                    27,
                    props.instance?.serviceManual.length
                  )}
                </p>
              </>
            ) : (
              <p>No Service Manual</p>
            )}
          </div>
        </div>
      </ModalBody>
      <style jsx>{`
        p {
          margin: 0;
        }
      `}</style>
    </ModalLayout>
  );
}

MobileMaintenanceCardDetails.propTypes = {
  instance: PropTypes.shape({
    id: PropTypes.string,
    createdAt: PropTypes.string,
    orgId: PropTypes.string,
    orgName: PropTypes.string,
    scheduleId: PropTypes.string,
    scheduleName: PropTypes.string,
    serviceManual: PropTypes.string,
    gatewayId: PropTypes.string,
    gatewayName: PropTypes.string,
    reminder: PropTypes.shape({
      id: PropTypes.string,
      maintenance_schedule_id: PropTypes.string,
      name: PropTypes.string,
      type: PropTypes.string,
      message: PropTypes.string,
      recurring: PropTypes.bool,
      trigger: PropTypes.shape({
        frequency: PropTypes.string,
        name: PropTypes.string,
        reminderMargin: PropTypes.number,
        type: PropTypes.oneOf(['conditional', 'calendar']),
        value: PropTypes.number,
      }),
      attachment: PropTypes.string,
    }),
    partsCost: PropTypes.string,
    serviceDate: PropTypes.string,
    enabled: PropTypes.bool,
    offset: PropTypes.number,
    startDate: PropTypes.string,
    endData: PropTypes.string,
    lastRun: PropTypes.string,
    pending: PropTypes.arrayOf(PropTypes.string),
    lastTriggerVal: PropTypes.number,
    preacknowledged: PropTypes.bool,
    triggerHistory: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
        createdAt: PropTypes.string, // Date Trigger column value
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        due: PropTypes.bool, // true === 'due', false === 'upcoming'
        maintenanceLogId: PropTypes.string, // when present === 'completed'
        serviceDate: PropTypes.string, // will be present if 'completed'
      })
    ),
    value: PropTypes.number,
  }),
  instanceType: PropTypes.oneOf(['complete', 'due', 'upcoming']),
  isOpen: PropTypes.bool,
  signalValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  timeDiff: PropTypes.number,
  timeDiffDisplayText: PropTypes.string,
  toggleModal: PropTypes.func,
};
